import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Building2, Mail, DollarSign, Calendar, Film, ListChecks, Tag } from 'lucide-react';
import type { PublisherConfig, SelfServiceFormData } from '../../types/selfService';
import { computePlaysBoost, selectPlaysBoostSources } from '../../types/selfService';
import { formatMoneySmart } from './shared/format';
import { tintFromAccent } from './steps/_shared';
import { getTargetingRow } from './targetingRow';
import type { CodeValidation } from './useCodeValidation';

interface CampaignSummarySidebarProps {
  config: PublisherConfig;
  creativeFileName?: string;
  // CTV-457: number of commercials with content. >1 → renders an aggregate
  // "N commercials" line in the sidebar instead of the single filename, so
  // multi-creative campaigns don't lose their other commercials at a glance.
  // Falls back to creativeFileName for the single-commercial case.
  commercialCount?: number;
  // Optional: when the wizard has run validation, the sidebar mirrors the
  // same plays-boost math as the review page so the count updates with
  // payment-plan / promo / referral changes.
  codeValidation?: CodeValidation;
}

const formatDate = (s?: string) => {
  if (!s) return '';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Row: React.FC<{ icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; label: string; value?: string; accentColor: string }> = ({
  icon: Icon, label, value, accentColor,
}) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
        style={{ backgroundColor: tintFromAccent(accentColor, 0.08), color: accentColor }}
      >
        <Icon className="w-2.5 h-2.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] font-medium uppercase tracking-[0.1em] text-slate-400">{label}</div>
        <div className="text-[12px] text-slate-900 font-medium tracking-tight truncate">{value}</div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; accentColor: string; children: React.ReactNode; visible: boolean }> = ({
  title, accentColor, children, visible,
}) => {
  if (!visible) return null;
  return (
    <div>
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.12em] mb-1 px-1"
        style={{ color: accentColor }}
      >
        {title}
      </div>
      <div className="rounded-xl bg-white border border-slate-200/80 px-3 py-2 divide-y divide-slate-100">
        {children}
      </div>
    </div>
  );
};

const CampaignSummarySidebar: React.FC<CampaignSummarySidebarProps> = ({ config, creativeFileName, commercialCount, codeValidation }) => {
  const accent = config.accentColor || config.primaryColor;
  const { control } = useFormContext<SelfServiceFormData>();
  // CTV-533: subscribe to each field by name. A no-name `useWatch({ control })`
  // re-renders on every keystroke in any field, which under the convergence
  // churn around step 2's first interaction (auto-set selectedProduct /
  // targetMarkets, draft-token mint, feasibility settle) was clipping the
  // campaign-name / budget inputs' RHF ref callbacks mid-keystroke and
  // dropping focus. The wizard already follows this named-subscription
  // pattern; this is the last consumer that needed migrating.
  const businessName = useWatch({ control, name: 'businessName' });
  const contactEmail = useWatch({ control, name: 'contactEmail' });
  const campaignName = useWatch({ control, name: 'campaignName' });
  const selectedProduct = useWatch({ control, name: 'selectedProduct' });
  const budgetRaw = useWatch({ control, name: 'budget' });
  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });
  const paymentPlan = useWatch({ control, name: 'paymentPlan' });
  const targetMarkets = useWatch({ control, name: 'targetMarkets' });
  const targetingModeRaw = useWatch({ control, name: 'targetingMode' });
  const geoMode = useWatch({ control, name: 'geoMode' });
  const selectedStates = useWatch({ control, name: 'selectedStates' });
  const selectedDMAs = useWatch({ control, name: 'selectedDMAs' });
  const geoTargetCenter = useWatch({ control, name: 'geoTargetCenter' });
  const geoTargetRadiusMiles = useWatch({ control, name: 'geoTargetRadiusMiles' });
  const geoTargetZips = useWatch({ control, name: 'geoTargetZips' });

  const product = selectedProduct ? config.products.find((p) => p.id === selectedProduct) : undefined;
  const cpm = product?.cpm ?? 0;
  const budget = (budgetRaw ?? null) as number | null;
  // CTV-635: no advertiser-facing service fee, the total is the budget.
  const totalCost = budget && budget > 0 ? budget : null;

  // CTV-640: mirror the review-page plays boost through the shared helpers.
  // The plan is unselected (null) until the buyer picks one on the pay step,
  // so the prepay bonus only shows once upfront is explicitly chosen — the
  // deliberate null-plan rule (contrast PaymentPlanSection's comparison
  // cards, which always merchandise it).
  const boostSources = selectPlaysBoostSources({
    promoPercent: codeValidation?.promo.state === 'valid' ? codeValidation.promo.discountPercent : null,
    referralPercent: codeValidation?.ref.state === 'valid' ? codeValidation.ref.defaultDiscountPercent : null,
    includePrepayBonus: paymentPlan === 'upfront',
    startDate,
    endDate,
  });
  const boost = computePlaysBoost(budget, cpm, boostSources);
  const impressions = budget && budget > 0 ? boost.total : null;

  const targetingRow = getTargetingRow(
    {
      targetMarkets,
      targetingMode: targetingModeRaw,
      geoMode,
      selectedStates,
      selectedDMAs,
      geoTargetCenter,
      geoTargetRadiusMiles,
      geoTargetZips,
    },
    config,
  );

  const businessVisible = !!(businessName || contactEmail);
  const campaignVisible = !!(campaignName || budget || startDate || endDate || selectedProduct || (targetMarkets?.length ?? 0) > 0);
  const creativeFileNameValue = creativeFileName;
  // Aggregate the multi-commercial case into a single line ("3 commercials")
  // so the sidebar stays compact regardless of slot count. The Review step
  // owns the full per-slot breakdown.
  const creativeValue = (commercialCount ?? 0) > 1
    ? `${commercialCount} commercials`
    : creativeFileNameValue;
  const creativeVisible = !!creativeValue;
  const anyVisible = businessVisible || campaignVisible || creativeVisible;

  return (
    <aside className="w-full">
      <div
        className="rounded-2xl border border-slate-200/80 bg-slate-50/40 backdrop-blur p-4"
        style={{ boxShadow: 'var(--card-shadow)' }}
      >
        <div className="flex items-center gap-2 mb-3 px-1">
          <ListChecks className="w-3.5 h-3.5" style={{ color: accent }} />
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: accent }}>
            Your campaign
          </div>
        </div>

        {!anyVisible && (
          <p className="text-[12px] text-slate-500 px-1 leading-relaxed">
            Your selections will show up here as you fill out the form.
          </p>
        )}

        <div className="space-y-3">
          <Section title="Business" accentColor={accent} visible={businessVisible}>
            <Row icon={Building2} label="Business" value={businessName} accentColor={accent} />
            <Row icon={Mail} label="Email" value={contactEmail} accentColor={accent} />
          </Section>

          <Section title="Campaign" accentColor={accent} visible={campaignVisible}>
            <Row icon={Tag} label="Name" value={campaignName} accentColor={accent} />
            <Row icon={DollarSign} label="Budget" value={budget && budget > 0 ? `$${formatMoneySmart(budget)}` : ''} accentColor={accent} />
            <Row icon={Calendar} label="Start" value={formatDate(startDate)} accentColor={accent} />
            <Row icon={Calendar} label="End" value={formatDate(endDate)} accentColor={accent} />
            <Row icon={targetingRow.icon} label={targetingRow.label} value={targetingRow.value} accentColor={accent} />
          </Section>

          <Section title="Commercial" accentColor={accent} visible={creativeVisible}>
            <Row icon={Film} label="Video" value={creativeValue} accentColor={accent} />
          </Section>
        </div>

        {/* Total */}
        {totalCost !== null && (
          <div
            className="mt-4 rounded-xl p-3"
            style={{ backgroundColor: tintFromAccent(accent, 0.06) }}
          >
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
                Total
              </span>
              <span className="text-[15px] font-semibold tracking-tight text-slate-900 tabular-nums">
                ${formatMoneySmart(totalCost)}
              </span>
            </div>
            {impressions !== null && (
              <div className="text-[11px] text-slate-500 tabular-nums">
                ~{impressions.toLocaleString()} streaming ad plays
                {boost.bonus > 0 && (
                  <span className="ml-1 font-semibold" style={{ color: accent }}>
                    (+{boost.bonus.toLocaleString()} bonus)
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default CampaignSummarySidebar;
